import { useCallback, useEffect, useState } from 'react'
import { Copy, Pause, Play, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@orisini/ui/components/button'
import { Card, CardContent, CardHeader, CardTitle } from '@orisini/ui/components/card'
import { useProductAuth } from '@orisini/ui/context/AuthContext'
import { ApiError } from '@orisini/ui/lib/api'
import { accountsApi, STATUS_LABEL, type AccountDto } from '../api'

export default function AccountsPage() {
  const { me } = useProductAuth()
  const [accounts, setAccounts] = useState<AccountDto[]>([])
  const [limit, setLimit] = useState({ used: 0, max: 1 })
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    try {
      const data = await accountsApi.list()
      setAccounts(data.accounts)
      setLimit(data.limit)
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't load accounts")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])


  async function onTogglePause(a: AccountDto) {
    try {
      if (a.paused) await accountsApi.resume(a.id)
      else await accountsApi.pause(a.id)
      await load()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't change status")
    }
  }

  async function onDelete(a: AccountDto) {
    try {
      const preview = await accountsApi.previewDelete(a.id)
      // Konfirmasi berbasis toast action — konvensi proyek melarang confirm().
      toast('Delete this account?', {
        description:
          preview.pinsToDelete > 0
            ? `${preview.pinsToDelete} unpublished posts will be deleted too.`
            : 'No active posts will be lost.',
        action: {
          label: 'Delete',
          onClick: () => {
            accountsApi
              .remove(a.id)
              .then(() => {
                toast.success(`Account "${a.label}" deleted`)
                return load()
              })
              .catch((err: unknown) =>
                toast.error(err instanceof ApiError ? err.message : "Couldn't delete"),
              )
          },
        },
      })
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't load account data")
    }
  }

  const full = limit.used >= limit.max

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Instagram accounts</h1>
          <p className="text-sm text-muted-foreground">
            {limit.used} of {limit.max} connected accounts used across all platforms on the {me?.plan.name} plan
          </p>
        </div>
      </header>

      {/*
        Akun TIDAK ditambahkan dari sini. Yang menentukan akun mana yang
        benar-benar terhubung adalah sesi browser di komputer user, jadi akun
        lahir saat agent selesai login dan melaporkan username yang terdeteksi.
        Form di web hanya akan melahirkan akun tanpa profil browser: terlihat
        ada di daftar, tapi tidak pernah bisa memposting.
      */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Add an account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            Open <strong>the Gramium app on your computer</strong> → the{' '}
            <strong>Add Instagram account</strong> button. A Chrome window opens; sign in as usual,
            then the account appears here automatically with its username.
          </p>
          <p>Your Instagram sign in is never sent to the server — the session stays on that computer.</p>
          {full && (
            <p className="text-amber-500">
              The account quota for the {me?.plan.name} plan is full. Upgrade to add accounts.
            </p>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : accounts.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No Instagram accounts yet — add one through the Gramium app on your computer.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {accounts.map((a) => {
            const status = STATUS_LABEL[a.status]
            return (
              <Card key={a.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <CardTitle className="truncate text-base">{a.label}</CardTitle>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {a.username ? `@${a.username}` : 'Not signed in'} ·{' '}
                        <span className={status.className}>{status.text}</span>
                      </p>
                    </div>
                    {a.paused && (
                      <span className="shrink-0 rounded bg-amber-500/15 px-2 py-0.5 text-xs text-amber-500">
                        Paused
                      </span>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <dt className="text-muted-foreground">Max/hour</dt>
                    <dd className="tabular-nums">{a.maxPostsPerHour}</dd>
                    <dt className="text-muted-foreground">Minimum gap</dt>
                    <dd className="tabular-nums">{a.minGapMinutes} minutes</dd>
                    <dt className="text-muted-foreground">Timezone</dt>
                    <dd>{a.timezone ?? `${me?.user.timezone} (follows account)`}</dd>
                  </dl>

                  <div className="rounded-md bg-muted/50 p-3">
                    <p className="mb-1.5 text-xs text-muted-foreground">
                      Browser profile folder on your computer
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="min-w-0 flex-1 truncate text-xs">{a.profileDir}</code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 shrink-0"
                        title="Copy"
                        onClick={() => {
                          void navigator.clipboard.writeText(a.profileDir)
                          toast.success('Copied')
                        }}
                      >
                        <Copy className="size-3.5" />
                      </Button>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Instagram sign in happens from the Gramium desktop app — the server never
                      stores your credentials.
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => void onTogglePause(a)}>
                      {a.paused ? <Play /> : <Pause />}
                      {a.paused ? 'Resume' : 'Pause'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => void onDelete(a)}
                    >
                      <Trash2 />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
